import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  useGridApiContext,
  useGridSelector,
  gridPageSelector,
} from '@mui/x-data-grid';
import { useState, useEffect, useRef } from 'react';

const paginationResets = new Set<string>();
const mobileResets = new Set<string>();

export function DataGridPaginationFooter({
  loading,
  hasNextPage,
}: {
  loading: boolean;
  hasNextPage?: boolean;
}) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const [showResetWarning, setShowResetWarning] = useState(false);
  const lastPageRef = useRef(0);
  const paginationKey = `grid-${Date.now()}`;

  useEffect(() => {
    if (
      lastPageRef.current > 0 &&
      page === 0 &&
      !paginationResets.has(paginationKey)
    ) {
      setShowResetWarning(true);
      paginationResets.add(paginationKey);

      setTimeout(() => {
        setShowResetWarning(false);
      }, 5000);

      setTimeout(() => {
        paginationResets.delete(paginationKey);
      }, 60000);
    }

    lastPageRef.current = page;
  }, [page, paginationKey]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '16px',
          width: '100%',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        padding: '12px 0',
        flexDirection: 'column',
      }}
    >
      {showResetWarning && (
        <Typography
          variant="body2"
          color="info.main"
          style={{ marginBottom: '8px' }}
        >
          Your pagination session has expired. You've been returned to the first
          page.
        </Typography>
      )}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          disabled={page === 0 || loading}
          onClick={() => apiRef.current.setPage(page - 1)}
          startIcon={<NavigateBefore />}
          variant="outlined"
          size="small"
          style={{ marginRight: '12px' }}
        >
          Previous
        </Button>

        <Typography variant="body2" style={{ margin: '0 12px' }}>
          Page {page + 1}
        </Typography>

        <Button
          disabled={!hasNextPage || loading}
          onClick={() => apiRef.current.setPage(page + 1)}
          endIcon={<NavigateNext />}
          variant="outlined"
          size="small"
          style={{ marginLeft: '12px' }}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

interface MobilePaginationProps {
  currentPage: number;
  totalPages: number;
  loading: boolean;
  styleClass: string;
  onPrevious: () => void;
  onNext: () => void;
  disablePrevious?: boolean;
  disableNext?: boolean;
}

export function CustomPaginationFooter({
  currentPage,
  totalPages,
  loading,
  styleClass,
  onPrevious,
  onNext,
  disablePrevious,
  disableNext,
}: MobilePaginationProps) {
  const [showResetWarning, setShowResetWarning] = useState(false);
  const lastPageRef = useRef(0);
  const paginationKey = `mobile-pagination-${Date.now()}`;

  const page = currentPage ?? 0;
  const total = totalPages ?? 0;
  const isDataReady = currentPage !== undefined && !loading;

  useEffect(() => {
    if (
      lastPageRef.current > 0 &&
      page === 0 &&
      !mobileResets.has(paginationKey)
    ) {
      setShowResetWarning(true);
      mobileResets.add(paginationKey);

      setTimeout(() => {
        setShowResetWarning(false);
      }, 5000);

      setTimeout(() => {
        mobileResets.delete(paginationKey);
      }, 60000);
    }

    lastPageRef.current = page;
  }, [page, paginationKey]);

  if (!isDataReady) {
    return (
      <Box className={styleClass} display="flex" justifyContent="center">
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <div className={styleClass}>
      {showResetWarning && (
        <Typography
          variant="caption"
          color="info.main"
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'center',
            marginBottom: '8px',
          }}
        >
          Your pagination session has expired. You've been returned to the first
          page.
        </Typography>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconButton
          onClick={onPrevious}
          disabled={
            disablePrevious !== undefined
              ? disablePrevious
              : page === 0 || loading
          }
        >
          <NavigateBefore />
        </IconButton>

        <Typography variant="body2">
          Page {page + 1}
          {total > 0 && ` of at least ${total}`}
        </Typography>

        <IconButton
          onClick={onNext}
          disabled={disableNext !== undefined ? disableNext : loading}
        >
          <NavigateNext />
        </IconButton>
      </div>
    </div>
  );
}
